// src/pages/Services/Login.jsx

import React, { useState, useEffect } from "react";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import axios from "../../axios";
import {
  Container,
  Paper,
  Avatar,
  Typography,
  TextField,
  Button,
  Alert,
  Link,
  Box,
  IconButton,
  Menu,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Fade,
  InputAdornment
} from "@mui/material";

// استيراد الأيقونات العصرية الجديدة
import { 
  PhoneAndroidOutlined, 
  LockOutlined, 
  Language, 
  LoginOutlined 
} from "@mui/icons-material";

import { useAuth } from '../../context/AuthContext';
import ForgotPasswordDialog from "./ForgotPasswordDialog";

export default function Login() {
  const navigate = useNavigate();
  const { setAuthData } = useAuth();
  const { t, i18n } = useTranslation();
  const [password, setPassword] = useState("");
  const [mobile, setmobile] = useState("");
  const [message, setMessage] = useState(null);
  const [severity, setSeverity] = useState("error");
  const [languageAnchor, setLanguageAnchor] = useState(null);
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  useEffect(() => {
    // 1. قراءة الجوال المتذكر
    const savedMobile = localStorage.getItem("rememberedMobile");
    if (savedMobile) {
      setmobile(savedMobile);
      setRememberMe(true);
    }

    // 2. إجبار المتصفح على تغيير الخلفية وإلغاء الشبكة القديمة تماماً عند دخول صفحة تسجيل الدخول
    const originalBackground = document.body.style.background;
    const originalBackgroundImage = document.body.style.backgroundImage;
    
    // تطبيق التدرج العصري الداكن والناعم مباشرة على الـ body
    document.body.style.background = 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 40%, #2e1065 100%)';
    document.body.style.backgroundImage = 'none'; // إلغاء الشبكة والمربعات القديمة فوراً

    // تنظيف التنسيق وإعادة الخلفية الأصلية للمشروع عند الانتقال للوحة التحكم (Dashboard)
    return () => {
      document.body.style.background = originalBackground;
      document.body.style.backgroundImage = originalBackgroundImage;
    };
  }, []);

  const handleLanguageClick = (event) => {
    setLanguageAnchor(event.currentTarget);
  };

  const handleLanguageClose = () => {
    setLanguageAnchor(null);
  };

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    handleLanguageClose();
  };

  const handleSubmit = async (e) => {
  e.preventDefault();
  setMessage(null);

  try {
    const response = await axios.post("/User/login", {
      mobile: mobile.trim(),
      password: password.trim(),
    });

    const {
      success,
      message,
      token,
      refreshToken,      
      companyId,
      branchId,
      permissions = []
    } = response.data;

    if (!success) {
      throw new Error(message || "❌ فشل تسجيل الدخول");
    }

    localStorage.setItem("token", token);
    localStorage.setItem("refreshToken", refreshToken); // 🟢 جديد
    localStorage.setItem("companyId", companyId);
    localStorage.setItem("branchId", branchId);

    if (rememberMe) {
      localStorage.setItem("rememberedMobile", mobile.trim());
    } else {
      localStorage.removeItem("rememberedMobile");
    }

    if (response.data.safeId) localStorage.setItem("safeId", response.data.safeId);
    else localStorage.removeItem("safeId");

    if (response.data.stockId) localStorage.setItem("stockId", response.data.stockId);
    else localStorage.removeItem("stockId");

    if (response.data.allowedSafeIds?.length > 0) {
      localStorage.setItem("allowedSafeIds", JSON.stringify(response.data.allowedSafeIds));
    } else {
      localStorage.removeItem("allowedSafeIds");
    }

    if (response.data.allowedStockIds?.length > 0) {
      localStorage.setItem("allowedStockIds", JSON.stringify(response.data.allowedStockIds));
    } else {
      localStorage.removeItem("allowedStockIds");
    }

    if (response.data.allowedBranchIds?.length > 0) {
      localStorage.setItem("allowedBranchIds", JSON.stringify(response.data.allowedBranchIds));
    } else if (response.data.branchId) {
      localStorage.setItem("allowedBranchIds", JSON.stringify([response.data.branchId]));
    } else {
      localStorage.removeItem("allowedBranchIds");
    }

    if (response.data.userId) localStorage.setItem("userId", response.data.userId);
    if (response.data.userName) localStorage.setItem("userName", response.data.userName);
    if (response.data.companyName) localStorage.setItem("companyName", response.data.companyName);
    if (response.data.mobile) localStorage.setItem("mobile", response.data.mobile);

    setAuthData({ permissions });

    setSeverity("success");
    setMessage(message || `✅ ${t('login.messages.loginSuccess')}`);

    setTimeout(() => navigate("/dashboard"), 800);

  } catch (err) {
    const serverMsg =
      err.response?.data?.message ||
      err.message ||
      `❌ ${t('login.messages.loginFailed')}`;

    setSeverity("error");
    setMessage(serverMsg);
  }
};

  return (
    <Box 
      sx={{ 
        // جعل الـ Box يطفو فوق الخلفية القديمة تماماً ويغطي الشاشة كاملة
        position: 'fixed !important',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100vw',
        height: '100vh',
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        zIndex: 9999, // قيمة عالية لتضمن تغطية أي تنسيق خارجي عتيق
        overflow: 'hidden',
        
        // خلفية عصرية حديثة جداً (تدرج ألوان متداخلة فخم ومريح للعين)
        background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 40%, #2e1065 100%) !important',
        
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0, left: 0, right: 0, bottom: 0,
          // هالات ضوئية ناعمة وعصرية (Aurora Effect) بدلاً من المربعات الحادة
          backgroundImage: `
            radial-gradient(circle at 20% 20%, rgba(99, 102, 241, 0.25) 0%, transparent 50%),
            radial-gradient(circle at 80% 80%, rgba(16, 185, 129, 0.15) 0%, transparent 50%)
          `,
          zIndex: 1,
        }
      }}
    >
      <Fade in timeout={1200}>
        <Container maxWidth="xs" sx={{ position: 'relative', zIndex: 10 }}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 4, sm: 5 },
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              borderRadius: '28px', 
              width: '100%',
              background: 'rgba(255, 255, 255, 0.98)', // تباين قوي ونظيف مع الخلفية الداكنة الجديدة
              boxShadow: '0 40px 80px -20px rgba(0, 0, 0, 0.6)',
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            {/* مؤشر ملون علوي رفيع جداً لإضفاء لمسة جمالية */}
            <Box sx={{ 
              height: '6px', 
              width: '100%', 
              background: 'linear-gradient(90deg, #4F46E5 0%, #10B981 100%)',
              position: 'absolute',
              top: 0, left: 0
            }} />

            {/* أيقونة اختيار اللغة */}
            <Box sx={{ position: 'absolute', top: 20, right: 20 }}>
              <IconButton
                onClick={handleLanguageClick}
                size="small"
                sx={{ 
                  color: 'primary.main',
                  bgcolor: 'rgba(79, 70, 229, 0.06)',
                  borderRadius: '12px',
                  p: 1,
                  '&:hover': { bgcolor: 'rgba(79, 70, 229, 0.12)' }
                }}
              >
                <Language fontSize="small" />
              </IconButton>
              <Menu
                anchorEl={languageAnchor}
                open={Boolean(languageAnchor)}
                onClose={handleLanguageClose}
                TransitionComponent={Fade}
                PaperProps={{
                  sx: { borderRadius: '14px', mt: 1, boxShadow: '0 10px 30px rgba(0,0,0,0.1)' }
                }}
              >
                <MenuItem onClick={() => changeLanguage('ar')} sx={{ fontWeight: 700, px: 3 }}>
                  {t('common.arabic')}
                </MenuItem>
                <MenuItem onClick={() => changeLanguage('en')} sx={{ fontWeight: 700, px: 3 }}>
                  {t('common.english')}
                </MenuItem>
              </Menu>
            </Box>

            {/* لوجو الحماية/القفل العصري الدائري */}
            <Avatar 
              sx={{ 
                mt: 1, mb: 2,
                background: 'linear-gradient(135deg, #4F46E5 0%, #3730A3 100%)', 
                width: 56, 
                height: 56,
                boxShadow: '0 12px 24px -4px rgba(79, 70, 229, 0.4)'
              }}
            >
              <LockOutlined fontSize="medium" />
            </Avatar>
            
            <Typography component="h1" variant="h5" sx={{ mb: 0.5, fontWeight: 800, color: '#0F172A', letterSpacing: -0.5 }}>
              {t('login.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 4, fontWeight: 600 }}>
              {t('login.welcome_subtitle') || 'نظام بروفي المتكامل'}
            </Typography>

            {message && (
              <Fade in={Boolean(message)}>
                <Alert 
                  severity={severity} 
                  variant="filled"
                  sx={{ width: "100%", mb: 3, borderRadius: '12px', fontWeight: 600 }}
                >
                  {message}
                </Alert>
              </Fade>
            )}

            <Box component="form" onSubmit={handleSubmit} sx={{ width: "100%" }}>
              
              <TextField
                margin="normal"
                fullWidth
                label={t('login.mobile')}
                value={mobile}
                onChange={(e) => setmobile(e.target.value)}
                required
                autoFocus
                variant="outlined"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneAndroidOutlined sx={{ color: 'text.secondary', fontSize: '1.2rem' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 2.5 }}
              />

              <TextField
                margin="normal"
                fullWidth
                label={t('login.password')}
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                variant="outlined"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlined sx={{ color: 'text.secondary', fontSize: '1.2rem' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 2.5 }}
              />

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, px: 0.5 }}>
                <FormControlLabel
                  control={
                    <Checkbox 
                      checked={rememberMe} 
                      onChange={(e) => setRememberMe(e.target.checked)} 
                      color="primary"
                      size="small"
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                      {t('login.remember_me') || 'تذكرني'}
                    </Typography>
                  }
                />
                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  onClick={() => setForgotPasswordOpen(true)}
                  sx={{ 
                    fontWeight: 700, 
                    textDecoration: 'none',
                    color: 'primary.main',
                    '&:hover': { color: 'primary.dark' }
                  }}
                >
                  {t('common.forgot_password')}
                </Link>
              </Box>

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                endIcon={<LoginOutlined sx={{ transform: i18n.language === 'ar' ? 'rotate(180deg)' : 'none' }} />} 
                sx={{ 
                  py: 1.6,
                  fontSize: '1rem',
                  fontWeight: 700,
                  mb: 3,
                  borderRadius: '14px',
                  background: 'linear-gradient(90deg, #4F46E5 0%, #6366F1 100%)',
                  boxShadow: '0 10px 25px -6px rgba(79, 70, 229, 0.4)',
                  '&:hover': {
                    background: 'linear-gradient(90deg, #3730A3 0%, #4F46E5 100%)',
                    boxShadow: '0 15px 30px -5px rgba(79, 70, 229, 0.5)',
                  }
                }}
              >
                {t('login.loginButton')}
              </Button>

              <Box textAlign="center">
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                  {t('login.noAccount')}{" "}
                  <Link 
                    component={RouterLink} 
                    to="/register" 
                    sx={{ 
                      fontWeight: 800, 
                      textDecoration: 'none',
                      color: 'secondary.main',
                      '&:hover': { color: 'secondary.dark', textDecoration: 'underline' } 
                    }}
                  >
                    {t('login.createAccount')}
                  </Link>
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Container>
      </Fade>
      <ForgotPasswordDialog 
        open={forgotPasswordOpen} 
        onClose={() => setForgotPasswordOpen(false)} 
      />
    </Box>
  );
}